import DashboardAnalyticsRangeSelector from '@/components/DashboardAnalyticsRangeSelector';
import {
  VerticalBarChart,
  formatCurrency,
  formatNumber,
} from '@/components/charts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from '@/components/ui/chart';
import { useAuthContext } from '@/contexts/AuthContext';
import { DashboardContext } from '@/contexts/DashboardContext';
import { UserContext } from '@/contexts/UserContext';
import { useAccountUsage } from '@/hooks/useAccountUsage';
import { adsAccountsColRef } from '@/services/AdsAccountsService';
import { getAdsAccountsUsage, getUsage } from '@/services/AnalyticsService';
import { analyticsRangeToDates } from '@/utils';
import { AdsAccount } from '@superfunnel/types';
import Label from '@superfunnel/ui-core/v2/Label';
import Switch from '@superfunnel/ui-core/v2/Switch';
import { getDocs } from 'firebase/firestore';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  BarChart as RechartsBarChart,
  XAxis,
  YAxis,
} from 'recharts';

// Dynamic chart config will be generated based on accounts
const generateAdsChartConfig = (accounts: Record<string, AdsAccount>) => {
  const config: ChartConfig = {};

  Object.entries(accounts).forEach(([accountId, account], index) => {
    // Generate different colors for each account
    const hue = (index * 137.5) % 360; // Golden angle for good color distribution
    config[`${accountId}_chargeable`] = {
      label: `${account.name} (Chargeable)`,
      color: `hsl(${hue}, 70%, 50%)`,
    };
    config[`${accountId}_nonChargeable`] = {
      label: `${account.name} (Non-Chargeable)`,
      color: `hsl(${hue}, 30%, 70%)`,
    };
  });

  return config;
};

export function UsageTab() {
  const { analyticsRange } = useContext(DashboardContext);
  const { isSupportAdmin } = useAuthContext();
  const { selectedAccount } = useContext(UserContext);
  const accountUsage = useAccountUsage(selectedAccount?.id);
  const [usageData, setUsageData] = useState<{
    dailyVisitors: {
      event: string;
      day: string;
      custom: number;
      totalCustom: number;
      uniqueCustom: number;
    }[];
    totalVisitors: number;
  } | null>(null);
  const [adsUsageData, setAdsUsageData] = useState<
    {
      clicks: number;
      impressions: number;
      cost: number;
      conversions: number;
      isChargeable: boolean;
      adsAccountId: string;
      date?: string;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [adsLoading, setAdsLoading] = useState(false);
  const [isCumulative, setIsCumulative] = useState(true);
  const [isAdsCumulative, setIsAdsCumulative] = useState(false);
  const [showNonChargeable, setShowNonChargeable] = useState(false);
  const [adsAccounts, setAdsAccounts] = useState<Record<string, AdsAccount>>(
    {},
  );

  useEffect(() => {
    if (!selectedAccount || !analyticsRange) return;

    const fetchUsage = async () => {
      setLoading(true);
      try {
        const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
        const data = await getUsage(selectedAccount.id, startDate, endDate);
        setUsageData(data);
      } catch (error) {
        console.error('Error fetching usage data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchAdsAccounts = async () => {
      try {
        const adsAccountsSnapshot = await getDocs(
          adsAccountsColRef(selectedAccount.id),
        );
        const adsAccountsMap: Record<string, AdsAccount> = {};
        adsAccountsSnapshot.docs.forEach((doc) => {
          adsAccountsMap[doc.id] = doc.data();
        });
        setAdsAccounts(adsAccountsMap);
      } catch (error) {
        console.error('Error fetching ads accounts:', error);
      }
    };

    const fetchAdsUsage = async () => {
      setAdsLoading(true);
      try {
        const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
        const data = await getAdsAccountsUsage(
          selectedAccount.id,
          startDate,
          endDate,
          'date', // Always use date aggregation for charts
        );
        setAdsUsageData(data);
      } catch (error) {
        console.error('Error fetching ads usage data:', error);
      } finally {
        setAdsLoading(false);
      }
    };

    fetchUsage();
    fetchAdsAccounts();
    fetchAdsUsage();
  }, [selectedAccount, analyticsRange]);

  // Transform data for chart display
  const chartData =
    usageData?.dailyVisitors.map((item, index) => {
      if (isCumulative) {
        const cumulativeVisitors = usageData.dailyVisitors
          .slice(0, index + 1)
          .reduce((sum, day) => sum + day.custom, 0);
        return {
          date: item.day,
          visitors: cumulativeVisitors,
        };
      }
      return {
        date: item.day,
        visitors: item.custom,
      };
    }) || [];

  // Transform ads usage data for chart - create segments per account
  const { adsChartData, adsChartConfig } = useMemo(() => {
    if (!adsUsageData) return { adsChartData: [], adsChartConfig: {} };

    const config = generateAdsChartConfig(adsAccounts);

    // Group by date first, then create individual account segments
    const groupedByDate = adsUsageData
      .filter((item) => item.date)
      .reduce(
        (acc, item) => {
          const date = item.date!;
          if (!acc[date]) {
            acc[date] = {
              date,
            };
          }

          const accountKey = `${item.adsAccountId}_${item.isChargeable ? 'chargeable' : 'nonChargeable'}`;

          // Only include non-chargeable if the toggle is on
          if (!item.isChargeable && !showNonChargeable) {
            return acc;
          }

          if (!acc[date][accountKey]) {
            acc[date][accountKey] = 0;
          }
          acc[date][accountKey] += item.cost;

          return acc;
        },
        {} as Record<string, any>,
      );

    // Convert to array and sort by date
    let chartData = Object.values(groupedByDate).sort(
      (a: any, b: any) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    // Apply cumulative calculation if enabled
    if (isAdsCumulative && chartData.length > 0) {
      const accountKeys = new Set<string>();

      // Collect all unique account keys
      chartData.forEach((item: any) => {
        Object.keys(item).forEach((key) => {
          if (key !== 'date') {
            accountKeys.add(key);
          }
        });
      });

      // Calculate cumulative values for each account key
      const cumulativeData = chartData.map((item: any, index: number) => {
        const cumulativeItem: any = { date: item.date };

        accountKeys.forEach((accountKey) => {
          cumulativeItem[accountKey] = chartData
            .slice(0, index + 1)
            .reduce((sum, prevItem) => sum + (prevItem[accountKey] || 0), 0);
        });

        return cumulativeItem;
      });

      chartData = cumulativeData;
    }

    return { adsChartData: chartData, adsChartConfig: config };
  }, [adsUsageData, adsAccounts, showNonChargeable, isAdsCumulative]);

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-center justify-between">
        <h3 className="text-base-900 text-lg font-medium">Usage Analytics</h3>
        <DashboardAnalyticsRangeSelector size="small" />
      </div>

      {/* Summary Section - Always visible */}
      <div className="space-y-4">
        <h4 className="text-base-900 text-lg font-medium">Summary</h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">Total Visitors</h4>
            {loading ? (
              <div className="space-y-2">
                <div className="bg-base-200 h-8 w-16 animate-pulse rounded"></div>
                <div className="bg-base-200 h-3 w-24 animate-pulse rounded"></div>
              </div>
            ) : usageData ? (
              <>
                <p className="text-2xl font-bold text-blue-600">
                  {formatNumber(usageData.totalVisitors)}
                </p>
                <p className="text-base-500 text-sm">in selected period</p>
              </>
            ) : (
              <>
                <p className="text-base-400 text-2xl font-bold">--</p>
                <p className="text-base-500 text-sm">in selected period</p>
              </>
            )}
          </div>
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">Average Daily</h4>
            {loading ? (
              <div className="space-y-2">
                <div className="bg-base-200 h-8 w-16 animate-pulse rounded"></div>
                <div className="bg-base-200 h-3 w-24 animate-pulse rounded"></div>
              </div>
            ) : usageData ? (
              <>
                <p className="text-2xl font-bold text-green-600">
                  {formatNumber(
                    Math.round(
                      usageData.totalVisitors /
                        Math.max(usageData.dailyVisitors.length, 1),
                    ),
                  )}
                </p>
                <p className="text-base-500 text-sm">visitors per day</p>
              </>
            ) : (
              <>
                <p className="text-base-400 text-2xl font-bold">--</p>
                <p className="text-base-500 text-sm">visitors per day</p>
              </>
            )}
          </div>
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">Peak Day</h4>
            {loading ? (
              <div className="space-y-2">
                <div className="bg-base-200 h-8 w-16 animate-pulse rounded"></div>
                <div className="bg-base-200 h-3 w-24 animate-pulse rounded"></div>
              </div>
            ) : usageData ? (
              <>
                <p className="text-2xl font-bold text-purple-600">
                  {formatNumber(
                    Math.max(...usageData.dailyVisitors.map((d) => d.custom)),
                  )}
                </p>
                <p className="text-base-500 text-sm">highest single day</p>
              </>
            ) : (
              <>
                <p className="text-base-400 text-2xl font-bold">--</p>
                <p className="text-base-500 text-sm">highest single day</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Resource & AI Usage Section - Current Billing Period */}
      <div className="space-y-4">
        <div>
          <h4 className="text-base-900 text-lg font-medium">
            Current Billing Period
          </h4>
          {accountUsage && (
            <p className="text-base-500 text-sm">
              {new Date(accountUsage.periodStart).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}{' '}
              -{' '}
              {new Date(accountUsage.periodEnd).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
          {/* Domains */}
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">Domains</h4>
            <p className="text-2xl font-bold text-blue-600">
              {accountUsage?.domains ?? '--'}
            </p>
            <p className="text-base-500 text-sm">connected domains</p>
          </div>

          {/* Pages */}
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">Pages</h4>
            <p className="text-2xl font-bold text-green-600">
              {accountUsage?.pages ?? '--'}
            </p>
            <p className="text-base-500 text-sm">total pages</p>
          </div>

          {/* Published Pages */}
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">Published Pages</h4>
            <p className="text-2xl font-bold text-purple-600">
              {accountUsage?.publishedPages ?? '--'}
            </p>
            <p className="text-base-500 text-sm">live pages</p>
          </div>

          {/* Members */}
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">Members</h4>
            <p className="text-2xl font-bold text-orange-600">
              {accountUsage?.members ?? '--'}
            </p>
            <p className="text-base-500 text-sm">team members</p>
          </div>

          {/* AI Credits */}
          <div className="bg-base-50 rounded-lg p-4">
            <h4 className="text-base-900 mb-1 font-medium">AI Credits Used</h4>
            <p className="text-2xl font-bold text-indigo-600">
              {Math.floor(accountUsage?.aiTokens?.spendTotal ?? 0)}
            </p>
            <p className="text-base-500 text-sm">this billing period</p>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-base-900 text-lg font-medium">
              Daily Visitors
            </h4>
            <p className="text-base-500 text-sm">
              {isCumulative ? 'Cumulative' : 'Daily'} unique visitors over time
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Label htmlFor="visitors-cumulative-switch">Cumulative</Label>
            <Switch
              id="visitors-cumulative-switch"
              checked={isCumulative}
              onCheckedChange={(checked) => setIsCumulative(checked)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-base-500">Loading usage data...</div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-90 w-full overflow-hidden">
            <VerticalBarChart
              className="h-[360px]"
              data={chartData.map((item) => ({
                label: item.date,
                value: item.visitors,
              }))}
              format="number"
              xAxis={{
                tickFormatter: (value: string) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                },
              }}
            />
          </div>
        ) : (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <div className="text-base-400 mb-2">
                <svg
                  className="mx-auto h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <p className="text-base-500">
                No usage data available for the selected period
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Ads Usage Chart Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-base-900 text-lg font-medium">
              Ads Spend Analytics
            </h4>
            <p className="text-base-500 text-sm">
              {isAdsCumulative ? 'Cumulative' : 'Daily'} cost from Google Ads
              landing pages
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {isSupportAdmin && (
              <div className="flex items-center space-x-2">
                <Label htmlFor="show-non-chargeable-switch">
                  Show Non-Chargeable
                </Label>
                <Switch
                  id="show-non-chargeable-switch"
                  checked={showNonChargeable}
                  onCheckedChange={(checked) => setShowNonChargeable(checked)}
                />
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Label htmlFor="spend-cumulative-switch">Cumulative</Label>
              <Switch
                id="spend-cumulative-switch"
                checked={isAdsCumulative}
                onCheckedChange={(checked) => setIsAdsCumulative(checked)}
              />
            </div>
          </div>
        </div>

        {adsLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-base-500">Loading ads usage data...</div>
          </div>
        ) : adsChartData.length > 0 ? (
          <div className="h-90 w-full overflow-hidden">
            <ChartContainer config={adsChartConfig} className="h-full w-full">
              <RechartsBarChart
                accessibilityLayer
                data={adsChartData}
                margin={{ top: 20, right: 0, bottom: 20, left: 0 }}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    });
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <ChartTooltip
                  cursor={false}
                  content={(...args) => {
                    const { active, payload, label } = args[0];
                    if (!active || !payload || !payload.length) return null;

                    const totalByCurrency = Object.entries(
                      payload.reduce(
                        (acc, entry) => {
                          const dataKey = entry.dataKey as string;
                          const [accountId, type] = dataKey.split('_');
                          const isChargeable = type === 'chargeable';
                          const account = adsAccounts[accountId];
                          const value = entry.value as number;

                          if (!acc[account.currencyCode]) {
                            acc[account.currencyCode] = {
                              chargeable: 0,
                              nonChargeable: 0,
                            };
                          }

                          if (isChargeable) {
                            acc[account.currencyCode].chargeable += value;
                          } else {
                            acc[account.currencyCode].nonChargeable += value;
                          }

                          return acc;
                        },
                        {} as Record<
                          string,
                          { chargeable: number; nonChargeable: number }
                        >,
                      ),
                    );

                    return (
                      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                        <div className="mb-2 font-medium text-gray-900">
                          {new Date(label).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>

                        {payload.map((entry) => {
                          // Parse the dataKey to get account info
                          const dataKey = entry.dataKey as string;
                          const [accountId, type] = dataKey.split('_');
                          const account = adsAccounts[accountId];
                          const isChargeable = type === 'chargeable';

                          return (
                            <div
                              key={dataKey}
                              className="flex items-center gap-2"
                            >
                              <div
                                className="h-3 w-3 rounded"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="font-medium text-gray-700">
                                {account.name}
                              </span>
                              <span className="text-sm text-gray-500">
                                (
                                {isChargeable ? 'Chargeable' : 'Non-Chargeable'}
                                )
                              </span>
                              <div className="ml-5 font-semibold text-gray-900">
                                {formatCurrency(
                                  entry.value as number,
                                  account.currencyCode,
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {totalByCurrency.map(([currency, totals], _, arr) => (
                          <div
                            key={currency}
                            className="mt-2 border-t pt-2 text-sm text-gray-700"
                          >
                            <div className="flex justify-between gap-2">
                              <span>
                                Total{arr.length > 1 ? ` (${currency})` : ''}:
                              </span>
                              <div className="flex flex-col items-end">
                                <span className="font-semibold text-gray-900">
                                  {formatCurrency(
                                    totals.chargeable || 0,
                                    currency,
                                  )}
                                </span>
                                {showNonChargeable && (
                                  <span className="text-xs font-normal text-gray-500">
                                    chargeable
                                  </span>
                                )}
                              </div>
                              {showNonChargeable && (
                                <div className="flex flex-col items-end">
                                  <span className="font-semibold text-gray-900">
                                    {formatCurrency(
                                      totals.nonChargeable || 0,
                                      currency,
                                    )}
                                  </span>
                                  <span className="text-xs font-normal text-gray-500">
                                    non-chargeable
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                {/* Render bars for each account dynamically */}
                {Object.entries(adsAccounts).map(([accountId]) => (
                  <Bar
                    key={`${accountId}_chargeable`}
                    dataKey={`${accountId}_chargeable`}
                    stackId="a"
                    fill={adsChartConfig[`${accountId}_chargeable`]?.color}
                    radius={[0, 0, 0, 0]}
                  />
                ))}
                {showNonChargeable &&
                  Object.entries(adsAccounts).map(([accountId]) => (
                    <Bar
                      key={`${accountId}_nonChargeable`}
                      dataKey={`${accountId}_nonChargeable`}
                      stackId="a"
                      fill={adsChartConfig[`${accountId}_nonChargeable`]?.color}
                      radius={[0, 0, 0, 0]}
                    />
                  ))}
              </RechartsBarChart>
            </ChartContainer>
          </div>
        ) : (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <div className="text-base-400 mb-2">
                <svg
                  className="mx-auto h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <p className="text-base-500">
                No ads usage data available for the selected period
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
